import {
  afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, input, viewChild
} from '@angular/core';
import { DrFutureMode } from '../../../services/dr-future.service';

/**
 * The UI says "evidence" / "alternative"; the asset files keep the provider's short
 * stems. Not visible UI, so the clips are not renamed (template `STEM` table).
 */
export const DR_FUTURE_AVATAR_STEM: Readonly<Record<DrFutureMode, string>> = { evidence: 'evi', alternative: 'alt' };
const AVATAR_BASE = '/assets/dr-future/avatars/avatar_';
/**
 * The clips bypass the Angular service worker (`ngsw-bypass`, the same switch the chat
 * and conference iframes use). A <video> loads by byte range and ngsw cannot serve range
 * requests: once the worker controls the page (every visit after the first) each clip's
 * first request was cancelled, then failed from ngsw-worker.js, and only the third
 * attempt returned 206 (provider CHANGES.md §1, 2026-10-25). A fresh profile never shows it.
 */
function clipSrc(mode: DrFutureMode, clip: string): string {
  return `${AVATAR_BASE}${DR_FUTURE_AVATAR_STEM[mode]}_${clip}.mp4?ngsw-bypass=true`;
}

export function drFutureAvatarPoster(mode: DrFutureMode): string {
  return `${AVATAR_BASE}${DR_FUTURE_AVATAR_STEM[mode]}.png`;
}

export function drFutureAvatarSmall(mode: DrFutureMode): string {
  return `${AVATAR_BASE}${DR_FUTURE_AVATAR_STEM[mode]}_small.png`;
}

type Clip = 'idle1' | 'idle2' | 'mov' | 'spk';
type Mood = 'idle' | 'think' | 'talk';
const CLIPS: Clip[] = ['idle1', 'idle2', 'mov', 'spk'];
/** Warm-up order within a mode: the idle loop and the speaking clip first. */
const WARM_ORDER: Clip[] = ['idle1', 'mov', 'spk', 'idle2'];

declare global {
  interface Window {
    /** Set before the page loads to get a timestamped trace of every avatar transition. */
    AVATAR_DEBUG?: boolean;
  }
}

/**
 * Dr. Future's animated portrait — the provider template's video engine (third
 * delivery `website-integrated3/`, 2026-10-25, CHANGES.md §1), ported 1:1. Public surface as in the
 * template: `setMode` (the `mode` input), `think()`, `talk(ms)`; `idle()` is ours.
 *
 * There is no speech: `think()` plays the "mov" clip while a question is in flight,
 * `talk(ms)` plays "spk" while an answer is on screen, and the idle loop mixes "mov"
 * in every fifth cycle so it is not the same two clips forever.
 *
 * Three states, deliberately separate: `want` is the clip the mood asks for, `cur` is
 * the clip on screen, `pending` is one we are trying to bring on. Collapsing those into
 * one variable is what froze the avatar in the first delivery: the moment the running
 * clip ended while the wanted one was not ready yet, there was nothing left to play and
 * the stage sat on a last frame — "while it thinks there is no video", "the video
 * disappears as the answer renders".
 *
 * Chromium (Arc, Chrome, Edge, Brave) SUSPENDS a <video> that is not playing and
 * reclaims its decoder; the bytes stay cached, the decoder is gone. Hence: only the
 * active mode's four clips exist (the other mode's are created on first switch),
 * `preload="none"` plus a sequential warm-up queue, each clip primed once (played and
 * paused at frame 0), the visual hand-over waits until the new clip is really running,
 * and every element carries the still as its `poster` (no decoded frame and no poster
 * paints nothing in Chromium and black in Safari). `prefers-reduced-motion` creates no
 * video element at all — which looks exactly like "the videos do not play".
 *
 * The video elements are created imperatively (as in the template) because the engine
 * swaps them by class at clip ends — that is DOM timing, not view state.
 */
@Component({
  selector: 'gh-dr-future-avatar',
  standalone: true,
  template: `
    <div class="drf-avatar-stage" #stage>
      <img class="drf-avatar-poster" #poster [src]="posterSrc" alt="">
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DrFutureAvatarComponent {
  readonly mode = input<DrFutureMode>('evidence');

  private stageRef = viewChild.required<ElementRef<HTMLElement>>('stage');
  private posterRef = viewChild.required<ElementRef<HTMLImageElement>>('poster');

  /** Initial poster; later swaps go straight to the element so they are instant. */
  readonly posterSrc = drFutureAvatarPoster('evidence');

  private clips: Record<DrFutureMode, Partial<Record<Clip, HTMLVideoElement>>> = { evidence: {}, alternative: {} };
  private stills: Partial<Record<DrFutureMode, HTMLImageElement>> = {};
  private ready = false;
  private destroyed = false;
  private reduced = false;

  private cur: HTMLVideoElement | null = null;
  private pending: HTMLVideoElement | null = null;
  private pendingSince = 0;
  private want: HTMLVideoElement | null = null;
  private currentMode: DrFutureMode = 'evidence';
  private mood: Mood = 'idle';
  private idleFlip = 0;
  private talkUntil = 0;
  private warming = false;
  private warned = false;
  private startedAt = 0;

  private readonly t0 = Date.now();
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  constructor() {
    afterNextRender(() => this.init());
    // The conversation decides the mode; the avatar follows it.
    effect(() => {
      const m = this.mode();
      if (this.ready) this.setMode(m);
    });
    inject(DestroyRef).onDestroy(() => this.teardown());
  }

  /**
   * A question is in flight. May be called before the clips exist (a reopened thread
   * with a pending turn): the mood is kept and the first clip honours it.
   */
  think(): void {
    this.log('think');
    this.mood = 'think';
    if (this.ready) this.next(true);
  }

  /** An answer has just arrived; "speak" for `ms`. */
  talk(ms: number): void {
    this.log('talk', `${ms}ms`);
    this.mood = 'talk';
    this.talkUntil = Date.now() + ms;
    if (this.ready) this.next(true);
  }

  /**
   * Back to the idle loop without speaking — the turn failed or the member left it.
   * (Ours: the template has no such path and keeps "thinking" after an error.)
   */
  idle(): void {
    if (this.mood === 'idle') return;
    this.log('idle');
    this.mood = 'idle';
    if (this.ready) this.next(true);
  }

  // ── set-up ─────────────────────────────────────────────────────────────────

  private init(): void {
    if (this.destroyed) return;
    this.reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.currentMode = this.mode();

    // The stills go first and alone. They are what the stage shows whenever no clip has
    // a decoded frame, so they must not queue behind megabytes of video.
    for (const m of ['evidence', 'alternative'] as DrFutureMode[]) {
      const img = new Image();
      img.src = drFutureAvatarPoster(m);
      this.stills[m] = img;
    }
    this.setStill(this.currentMode);
    this.ready = true;
    if (this.reduced) return;

    this.ensureMode(this.currentMode);
    this.warm();
    this.next(false);                   // claim the first clip now, not in a second's time
    // Safety net, and the only check that does not need to know which fault it was:
    // nothing in this UI ever asks the avatar to stop, so a stage with no clip running
    // is a fault by definition. One second of stillness and the loop restarts.
    this.tickTimer = setInterval(() => this.tick(), 1000);
  }

  private setMode(m: DrFutureMode): void {
    if (m === this.currentMode && this.cur) return;
    this.log('setMode', m);
    this.currentMode = m;
    this.setStill(m);
    if (this.reduced) return;
    this.ensureMode(m);
    // The running clip is NOT blanked here. It keeps playing until the new mode's clip
    // has a frame, so a mode switch cannot open a hole either.
    this.warm();
    this.next(true);
  }

  /** Swapped only once the new still is decoded, so a mode switch never shows the empty stage. */
  private setStill(m: DrFutureMode): void {
    const img = this.stills[m];
    const apply = () => { if (!this.destroyed) this.posterRef().nativeElement.src = drFutureAvatarPoster(m); };
    if (img && img.complete && img.naturalWidth) apply();
    else if (img) img.addEventListener('load', apply, { once: true });
    else apply();
  }

  /** Creates one mode's four clips, once. Only the active mode's exist until a switch. */
  private ensureMode(m: DrFutureMode): void {
    if (this.reduced || this.clips[m].idle1) return;
    const stage = this.stageRef().nativeElement;
    for (const n of CLIPS) {
      const v = document.createElement('video');
      v.className = 'drf-avatar-video';
      v.muted = true;
      v.defaultMuted = true;            // WebKit reads the attribute, not only the property
      v.playsInline = true;
      v.loop = false;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.setAttribute('disablepictureinpicture', '');
      v.setAttribute('aria-hidden', 'true');
      // The still behind the element itself: a <video> with no decoded frame and no
      // poster paints nothing in Chrome and black in Safari.
      v.poster = drFutureAvatarPoster(m);
      // NOT "auto": eight elements preloading at once put roughly four megabytes through
      // six connections, so the clip the UI asked for next was always the one still
      // queued. The warm-up queue sets the order instead.
      v.preload = 'none';
      // Assigned AFTER `preload`, so attaching the source does not start a transfer the
      // warm-up queue has not scheduled yet (provider's app.js of 2026-10-23 17:49).
      v.src = clipSrc(m, n);
      v.dataset['clip'] = `${m}/${n}`;
      v.addEventListener('ended', () => { if (v === this.cur) { this.log('ended', this.id(v)); this.next(false); } });
      v.addEventListener('loadeddata', () => {
        v.dataset['fails'] = '0';
        this.log('loadeddata', this.id(v), `want=${this.id(this.want)}`);
        if (v === this.want && v !== this.cur) this.show(v, true);
        else if (v === this.pending) this.show(v, true);
        // Already rolling but never handed the stage over, because it had no picture
        // when it started: take it now.
        else if (v === this.want && !v.paused) this.handOver(v);
      });
      v.addEventListener('error', () => this.clipFailed(v));
      stage.appendChild(v);
      this.clips[m][n] = v;
    }
    this.log('created clips for', m);
  }

  private tick(): void {
    if (this.destroyed) return;
    if (this.pending && Date.now() - this.pendingSince < 3000) return;
    if (this.pending) { this.log('pending stalled', this.id(this.pending)); this.pending = null; }
    // A play() that has not resolved yet still reports `paused`. Without this window the
    // net would "rescue" a clip that is in the middle of starting normally.
    if (Date.now() - this.startedAt < 2000) return;
    if (!this.cur || this.cur.paused || this.cur.ended) { this.log('stalled, restarting'); this.next(false); }
  }

  // ── warm-up ────────────────────────────────────────────────────────────────

  /** One clip at a time, in the order they are needed: the current mode first, the other last. */
  private warmOrder(): HTMLVideoElement[] {
    const other: DrFutureMode = this.currentMode === 'evidence' ? 'alternative' : 'evidence';
    const byMode = (m: DrFutureMode) => WARM_ORDER.map(n => this.clips[m][n]);
    // Only modes that have been created — the other mode's clips do not exist until
    // somebody switches to it.
    return [...byMode(this.currentMode), ...byMode(other)].filter((v): v is HTMLVideoElement => !!v);
  }

  private warm(): void {
    if (this.warming || this.destroyed) return;
    const v = this.warmOrder().find(x => !x.dataset['failed'] && x.preload === 'none');
    if (!v) return;
    this.warming = true;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      v.removeEventListener('canplaythrough', onReady);
      v.removeEventListener('error', done);
      this.warming = false;
      this.warm();
    };
    const onReady = () => { this.prime(v); done(); };
    v.addEventListener('canplaythrough', onReady);
    v.addEventListener('error', done);
    this.later(done, 8000);             // a stalled clip must not halt the queue behind it
    this.fetchClip(v);
  }

  /**
   * Forces the decoder up and leaves a decoded first frame behind, so the clip can be
   * shown instantly later instead of after a decoder initialisation. A paused element
   * with a decoded frame also paints that frame rather than nothing.
   */
  private prime(v: HTMLVideoElement): void {
    if (!v || v === this.cur || v === this.pending || v.dataset['primed'] || v.dataset['failed']) return;
    v.dataset['primed'] = '1';
    this.attempt(v, () => {
      if (v === this.cur) return;       // the UI claimed it in the meantime
      v.pause();
      try { v.currentTime = 0; } catch { /* not seekable */ }
      this.log('primed', this.id(v));
    });
  }

  /**
   * Starts one clip's transfer — exactly one.
   *
   * Moving `preload` from "none" to "auto" is itself the instruction to fetch, for an
   * element that already has a `src`. Calling `load()` as well starts a SECOND transfer
   * and aborts the first, so every clip was requested twice: once cancelled at zero
   * bytes, once served in full (the `(canceled)` row per clip in the network log, and on
   * a slow line a visible delay, because the transfer the UI waits for is thrown away).
   * Provider delivery 4 of 2026-10-26: cold cache 6 requests with 2 duplicate downloads
   * → 4 requests, one per clip.
   *
   * `load()` has one legitimate use here and it is not this one: resetting an element
   * that has errored or has no source at all. `again()` and `clipFailed()` do that.
   */
  private fetchClip(v: HTMLVideoElement | null): void {
    if (!v || v.dataset['failed'] || v.preload === 'auto') return;
    this.log('fetch', this.id(v));
    v.preload = 'auto';
  }

  // ── playing ────────────────────────────────────────────────────────────────

  private isReady(v: HTMLVideoElement | null | undefined): v is HTMLVideoElement {
    return !!v && !v.dataset['failed'] && v.readyState >= 2;
  }

  /** Bring `v` on screen and start it. Called only with a clip that has a frame. */
  private show(v: HTMLVideoElement | null, fade: boolean): void {
    if (!v || this.destroyed) return;
    if (v === this.pending) this.pending = null;
    if (v === this.cur) {
      // The same clip again, because `pick()` keeps asking for `mov` for as long as the
      // mood is "think" and for `spk` for as long as it is "talk". Replaying it is a
      // rewind, not a swap. Returning early here is what froze the avatar for the rest
      // of the think or talk window the moment the clip ran out.
      if (!v.paused && !v.ended) return;
      try { v.currentTime = 0; } catch { /* not seekable */ }
      this.log('replay', this.id(v));
      this.attempt(v, () => { /* already on stage */ });
      return;
    }
    // `drf-fade`, NOT the template's `fade`: Bootstrap ships `.fade:not(.show) { opacity: 0 }`,
    // and with the bare class the first cross-faded clip switched the WHOLE stage — still and
    // clips — to opacity 0 until the next hard cut took the class off again (the "still pops
    // up, then only the ring background, then the video" report of 2026-10-26).
    this.stageRef().nativeElement.classList.toggle('drf-fade', !!fade);
    if (v.ended || v.currentTime > 0) { try { v.currentTime = 0; } catch { /* not seekable */ } }
    this.log('show', this.id(v));
    this.attempt(v);
    this.again(v, 1);
  }

  /**
   * The stage changes hands only when the new clip is REALLY running and really has a
   * picture. It used to change hands on a FAILED play() as well, and then an element that
   * paints nothing sat on top of a clip that was working: the avatar faded in and straight
   * back out. `cur` moves here too, so it always names something that was on screen.
   */
  private handOver(v: HTMLVideoElement): void {
    if (!v || v === this.cur || v.readyState < 2 || this.destroyed) return;
    const old = this.cur;
    this.cur = v;
    v.classList.add('on');
    this.log('handover', this.id(v));
    if (old && old !== v) {
      old.classList.remove('on');
      this.later(() => {
        if (old === this.cur) return;
        old.pause();
        // Rewound here, hidden and fully buffered. Seeking a clip that is still
        // arriving restarts its transfer.
        try { old.currentTime = 0; } catch { /* not seekable */ }
      }, 300);
    }
  }

  /**
   * One play() in flight per element, ever. A second play() on an element whose first
   * has not settled makes that first one reject with AbortError — a failure this code
   * manufactured for itself. `show`, `again` and `prime` all go through here.
   */
  private attempt(v: HTMLVideoElement, onPlaying?: (v: HTMLVideoElement) => void): void {
    if (!v || v.dataset['playing'] === '1' || this.destroyed) return;
    v.dataset['playing'] = '1';
    this.startedAt = Date.now();
    const settle = (err: unknown) => {
      v.dataset['playing'] = '';
      if (this.destroyed) return;
      if (err) { this.playRejected(v, err); return; }
      (onPlaying ?? (x => this.handOver(x)))(v);
    };
    const p = v.play();
    if (p && typeof p.then === 'function') p.then(() => settle(null)).catch(settle);
    else settle(null);
  }

  /**
   * Keep asking, with backoff, for as long as this is still the clip the mood wants. Only
   * an element that has actually failed or has no source is reloaded — never one that is
   * still transferring, because load() aborts that transfer and starts over.
   */
  private again(v: HTMLVideoElement, n: number): void {
    if (n > 6) return;
    this.later(() => {
      if (this.want !== v || v === this.cur || !v.paused) return;
      if (v.error || v.networkState === v.NETWORK_NO_SOURCE) {
        v.preload = 'auto';
        v.load();
      }
      this.attempt(v);
      this.again(v, n + 1);
    }, 400 * n);
  }

  /**
   * What the mood wants. If it can play now it plays now; if not, whatever is already
   * running keeps running and `loadeddata` brings the wanted clip on the moment it can.
   */
  private request(v: HTMLVideoElement | null, fade: boolean): void {
    this.want = v;
    if (!v) return;
    if (this.isReady(v)) { this.show(v, fade); return; }
    this.pending = v;
    this.pendingSince = Date.now();
    this.fetchClip(v);
    this.log('waiting for', this.id(v));
    if (this.cur && !this.cur.paused && !this.cur.ended) return;   // motion continues meanwhile
    const bridge = CLIPS.map(n => this.clips[this.currentMode][n]).find(x => this.isReady(x) && x !== v);
    if (bridge) { this.log('bridging with', this.id(bridge)); this.show(bridge, fade); }
  }

  private playRejected(v: HTMLVideoElement, err: unknown): void {
    const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : String(err);
    this.log('play rejected', this.id(v), name);
    if (name === 'AbortError') return;  // superseded by the next clip; not a fault
    if (this.warned) return;
    this.warned = true;
    console.warn(`[avatar] ${this.id(v)} did not start: ${name}`
      + (name === 'NotAllowedError'
        ? ' — autoplay is blocked for this page. Safari: Settings for This Website > Auto-Play. On iOS, Low Power Mode blocks muted video too.'
        : ''));
  }

  private clipFailed(v: HTMLVideoElement): void {
    const n = (Number(v.dataset['fails']) || 0) + 1;
    v.dataset['fails'] = String(n);
    if (v === this.pending) this.pending = null;
    if (v === this.cur) this.cur = null;
    // A load failure is usually transient: an aborted range request, a service worker
    // that cannot serve media, a dropped connection. Marking the clip dead on the first
    // one left the loop with whichever clip happened to get through — or with none, and
    // then the avatar is simply gone. Three attempts, with a reload between them.
    if (n < 4) {
      this.log(`clip error ${n}, retrying`, this.id(v));
      this.later(() => {
        if (v.dataset['failed'] === '1' || this.destroyed) return;
        v.preload = 'auto';
        v.load();
      }, 500 * n);
    } else {
      v.dataset['failed'] = '1';
      console.warn(`[avatar] ${this.id(v)} failed to load ${n} times — giving up on it`);
    }
    const alt = CLIPS.map(x => this.clips[this.currentMode][x]).find(x => this.isReady(x));
    if (alt) this.show(alt, false);
  }

  // ── choosing ───────────────────────────────────────────────────────────────

  private pick(): Clip {
    if (this.mood === 'talk' && Date.now() > this.talkUntil) this.mood = 'idle';
    if (this.mood === 'think') return 'mov';
    if (this.mood === 'talk') return 'spk';
    this.idleFlip++;
    return this.idleFlip % 5 === 0 ? 'mov' : (this.idleFlip % 2 ? 'idle1' : 'idle2');
  }

  private next(fade: boolean): void {
    if (this.reduced || this.destroyed) return;
    let v: HTMLVideoElement | undefined = this.clips[this.currentMode][this.pick()];
    // A clip that could not be loaded is skipped rather than ending the loop.
    if (v && v.dataset['failed']) v = undefined;
    if (!v) v = CLIPS.map(n => this.clips[this.currentMode][n]).find(x => !!x && !x.dataset['failed']);
    this.request(v ?? null, fade);
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  private id(v: HTMLVideoElement | null | undefined): string {
    return v ? (v.dataset['clip'] ?? '?') : 'none';
  }

  /**
   * The trace goes through console.warn, not console.log: the production build replaces
   * console.log/info/debug with a no-op (libs/shared LoggerService's console filter), and
   * a trace somebody switched on by hand must not vanish there.
   */
  private log(...args: unknown[]): void {
    if (window.AVATAR_DEBUG) {
      console.warn(`[avatar +${((Date.now() - this.t0) / 1000).toFixed(2)}s]`, ...args);
    }
  }

  private later(fn: () => void, ms: number): void {
    const t = setTimeout(() => { this.timers.delete(t); fn(); }, ms);
    this.timers.add(t);
  }

  private teardown(): void {
    this.destroyed = true;
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = null;
    this.timers.forEach(t => clearTimeout(t));
    this.timers.clear();
    for (const m of ['evidence', 'alternative'] as DrFutureMode[]) {
      for (const v of Object.values(this.clips[m])) {
        if (!v) continue;
        v.pause();
        v.removeAttribute('src');
        v.load();
      }
    }
  }
}
